import type { ZodType } from 'zod/v4';

import type {
  AbstractResponse,
  BaseEndpointOptions,
  BuilderConfig,
  BuilderInstance,
  EndpointHandler,
  EndpointOptions,
  StreamHandler,
} from '../types.js';

/**
 * Context passed to every mock function. Mirrors what the real handler
 * receives, minus signal/headers (not interesting for mocks, but still
 * carried through for parity with `BuilderInstance`).
 */
export interface MockContext {
  urlParams?: Record<string, unknown>;
  params?: Record<string, unknown>;
  data?: unknown;
  signal?: AbortSignal | null;
  headers?: Record<string, string>;
}

export type MockEndpointOptions = EndpointOptions & {
  mock: (ctx: MockContext) => unknown | Promise<unknown>;
};
/**
 * Stream-mock options. Discriminated on `fullResponse` so the mock function's
 * return shape stays in sync with what the real declareStream handler would
 * return (Blob vs. AbstractResponse<Blob>).
 */
export type MockStreamOptions =
  | (BaseEndpointOptions & {
      fullResponse?: false;
      mock: (ctx: MockContext) => Blob | Promise<Blob>;
    })
  | (BaseEndpointOptions & {
      fullResponse: true;
      mock: (
        ctx: MockContext,
      ) => AbstractResponse<Blob> | Promise<AbstractResponse<Blob>>;
    });

/**
 * Builder shape produced by `mockBuilder`. Mirrors `BuilderInstance` for
 * the methods that matter (declareEndpoint / declareMultipart / declareStream),
 * so downstream code (hooks, react-query clients, etc.) is blind to whether
 * it's talking to a real API or a mock.
 */
export interface MockBuilderInstance<
  UseDiscriminator extends boolean = false,
> extends Pick<
  BuilderInstance<UseDiscriminator>,
  'provideClient' | 'getClient'
> {
  declareEndpoint<const O extends MockEndpointOptions>(
    options: O,
  ): EndpointHandler<O, UseDiscriminator>;
  declareMultipart<const O extends MockEndpointOptions>(
    options: O,
  ): EndpointHandler<O, UseDiscriminator>;
  declareStream<const O extends MockStreamOptions>(
    options: O,
  ): StreamHandler<O, UseDiscriminator>;
}

/**
 * Builder that short-circuits the client layer. Each endpoint REQUIRES a
 * `mock` function; responses are validated against the endpoint's schemas
 * so mocks cannot drift from the contract. Mirrors `BuilderInstance` so
 * consumers (hooks, react-query clients) are blind to whether they're
 * talking to a mock or a real API.
 *
 * @example
 * ```ts
 * const api = mockBuilder({})
 * const getUser = api.declareEndpoint({
 *   method: 'GET',
 *   url: '/users/$id',
 *   responseSchema: userSchema,
 *   mock: ({ urlParams }) => ({ id: String(urlParams?.id), name: 'Mock' }),
 * })
 * const user = await getUser({ urlParams: { id: '123' } })
 * ```
 */
export function mockBuilder<UseDiscriminator extends boolean = false>(
  config: BuilderConfig<UseDiscriminator> = {} as BuilderConfig<UseDiscriminator>,
): MockBuilderInstance<UseDiscriminator> {
  const prefixUrl = config.prefixUrl ?? '';

  function makeHandler<O extends MockEndpointOptions | MockStreamOptions>(
    options: O,
  ) {
    const handler = async (params: MockContext = {}) => {
      if (options.urlParamsSchema) {
        (options.urlParamsSchema as ZodType).parse(params.urlParams);
      }
      if (options.querySchema) {
        (options.querySchema as ZodType).parse(params.params ?? {});
      }
      if (options.requestSchema) {
        (options.requestSchema as ZodType).parse(params.data);
      }

      if (typeof options.mock !== 'function') {
        throw new Error(
          `[mockBuilder] Endpoint "${options.method} ${options.url}" was called without a \`mock\` function. ` +
            'Every endpoint declared via mockBuilder MUST provide a `mock` handler.',
        );
      }

      const result = await options.mock(params);

      if ('responseSchema' in options && options.responseSchema) {
        return (options.responseSchema as ZodType).parse(result);
      }
      return result;
    };
    const withUrl = { ...options, url: prefixUrl + options.url };
    (handler as unknown as { config: O }).config = withUrl as O;
    return handler as unknown as
      | EndpointHandler<never, UseDiscriminator>
      | StreamHandler<never, UseDiscriminator>;
  }

  return {
    provideClient: () => {
      /* no-op for mocks */
    },
    getClient: () => {
      throw new Error(
        '[mockBuilder] No real client — mocks short-circuit the client layer.',
      );
    },
    declareEndpoint:
      makeHandler as unknown as MockBuilderInstance<UseDiscriminator>['declareEndpoint'],
    declareMultipart:
      makeHandler as unknown as MockBuilderInstance<UseDiscriminator>['declareMultipart'],
    declareStream:
      makeHandler as unknown as MockBuilderInstance<UseDiscriminator>['declareStream'],
  };
}
